import { lazy, Suspense, useEffect, useState } from "react";
import { EmptyState, Segmented, Spinner } from "@agentsaas/ui";
import { useT } from "../app/i18n";
import { modelFormat } from "../assets/viewer-model";
import type { OpenFile } from "./store";

const GlbViewer = lazy(() => import("../assets/GlbViewer"));

const MEDIA = { gltf: "model/gltf-binary", fbx: "model/fbx", obj: "model/obj" } as const;

/** 文件内容 → 浏览器里可加载的 blob URL：预签名下载地址直接用；内联 base64 / 文本转成 Blob */
export function modelSource(file: Pick<OpenFile, "path" | "binary" | "content">): { url: string; blob: Blob | null } | null {
  if (file.binary?.url) return { url: file.binary.url, blob: null };
  const fmt = modelFormat(null, file.path);
  const type = file.path.toLowerCase().endsWith(".gltf") ? "model/gltf+json" : fmt ? MEDIA[fmt] : "application/octet-stream";
  if (file.binary?.base64 !== undefined) {
    const bin = atob(file.binary.base64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return { url: "", blob: new Blob([bytes], { type }) };
  }
  if (!file.binary) return { url: "", blob: new Blob([file.content], { type }) };
  return null;
}

/**
 * 文件树里打开的 3D 模型（.glb / .gltf，以及 .fbx / .obj）：通过 fs 读到字节后用查看器打开。
 * 文本格式（.gltf / .obj）可以切回源码编辑（onSource）。
 * 注意：.gltf 引用的外部 .bin / 贴图用相对路径，blob URL 下解析不到——只有内嵌缓冲区的 .gltf 能完整显示。
 */
export function ModelFileView({ file, onSource }: { file: OpenFile; onSource?(): void }) {
  const { t } = useT();
  const [url, setUrl] = useState<string | null>(null);
  const src = file.binary?.url ?? file.binary?.base64 ?? file.content;
  useEffect(() => {
    const s = modelSource(file);
    if (!s) return setUrl(null);
    if (!s.blob) return setUrl(s.url);
    const u = URL.createObjectURL(s.blob);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
    // 只在内容变化时重建
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file.path, src]);
  return (
    <div className="fileview fileview--model" data-testid="file-view" data-path={file.path} data-view="model">
      <div className="fileview__bar">
        <span className="ellipsis grow small muted" title={file.path}>
          {file.path}
        </span>
        {onSource ? (
          <Segmented
            label={t("model3d.file.mode")}
            value="3d"
            onChange={(v) => v === "source" && onSource()}
            options={[
              { value: "3d", label: t("model3d.file.view_3d") },
              { value: "source", label: t("model3d.file.view_source") },
            ]}
          />
        ) : null}
      </div>
      {url ? (
        <Suspense fallback={<Spinner label={t("common.loading")} />}>
          <GlbViewer key={url} url={url} name={file.name} fileName={file.path} />
        </Suspense>
      ) : (
        <EmptyState icon="cube" title={t("asset.preview_unavailable")} />
      )}
    </div>
  );
}
